import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@user/store'
import VueRouterMiddleware, { createMiddleware, middleware } from 'vue-router-middleware'

import { actionTypes, mutationTypes } from '@base/store/store-types'
import { getToken } from '@base/store'
import NotFoundPage from '@base/components/NotFoundPage.vue'
import SettingsView from '@base/views/SettingsView.vue'
import BasePage from '@user/views/BasePage.vue'
import SalesView from '@user/views/SalesView.vue'
import { webConfig } from '@base/configs'
import InstagramMediaView from '@base/views/InstagramMediaView'
import ProductSheetsView from '@user/views/ProductSheetsView.vue'
import ProductSheetDetailView from '@user/views/ProductSheetDetailView.vue'
import ProductSheetBaseView from '@user/views/ProductSheetBaseView.vue'
import ProductsView from '@user/views/ProductsView.vue'
import ProductSalesView from '@user/views/ProductSalesView.vue'
import PaymentReportView from "@user/views/PaymentReportView.vue";
import CreateBookingView from "@base/views/CreateBookingView.vue";
import BookingRequestView from "@user/views/BookingRequestView.vue";
import ExtendBookingView from "@base/views/ExtendBookingView.vue";
import BookingPaymentRequestView from "@base/views/BookingPaymentRequestView.vue";
import MarketTermsView from "@base/views/MarketTermsView.vue";
import {
  canContinueBookingRequestExtension, canContinueFinalizeBookingPaymentRequest,
  canExtendBookingRequest,
  canFinalizeBookingPaymentRequest
} from "@base/utils";
import i18n from "@base/i18n";

// Set marketId header to axios from session storage
const checkMarketId = (token) => {
  if (!store.state.marketId) {
    store.commit(mutationTypes.SET_MARKET_HEADER, token.marketId ? token.marketId : '')
  }
}

// Check if user can have an access to market
const checkMarketAccess = (next) => {
  const token = getToken('CustomerToken')
  if ((!token.flexposApiValid && (token.zettleIntegration && !token.zettleApiValid)) || !token.salesAccess) {
    store.dispatch(actionTypes.LOGOUT_CUSTOMER)
  } else {
    next()
  }
}

const checkIndividualizedItemsAccess = (next) => {
  const token = getToken('CustomerToken')
  if (token.individualizedItemsAccess && (token.isFlealoverSoloFeature || token.flexposApiValid || (token.zettleIntegration && token.zettleApiValid))) {
    next()
  } else {
    next({ name: 'not-found' })
  }
}

const checkPaymentsFeature = (next) => {
  const token = getToken('CustomerToken')
  if (token.paymentsFeature) {
    next()
  } else {
    next({ name: 'not-found' })
  }
}

const checkCustomerAccessToProductCreation = (next) => {
  const token = getToken('CustomerToken')
  if (token.customerAccessToProductCreation) {
    next()
  } else {
    next({ name: 'not-found' })
  }
}

const checkOnlineBookingFeature = (next) => {
  const token = getToken('CustomerToken')
  if (token.onlineBookingFeature) {
    next()
  } else {
    next({ name: 'not-found' })
  }
}

const checkGatewayApiValid = (next) => {
  const token = getToken('CustomerToken')
  if (token.gatewayApiValid) {
    next()
  } else {
    next({ name: 'not-found' })
  }
}

const checkGatewayValidOrOfflinePayments = (next) => {
  const token = getToken('CustomerToken')
  if (token.gatewayApiValid || token.allowOfflinePayments) {
    next()
  } else {
    next({ name: 'not-found' })
  }
}

const checkShowPaymentTerms = (next) => {
  const token = getToken('CustomerToken')
  if (token.showPaymentTerms) {
    next()
  } else {
    next({ name: 'not-found' })
  }
}

const checkCustomerAccessToBooking = (next) => {
  const token = getToken('CustomerToken')
  if (token.customerAccessToBooking) {
    next()
  } else {
    next({ name: 'not-found' })
  }
}

const checkCustomerAccessToBookingPaymentRequests = (next) => {
  const token = getToken('CustomerToken')
  if (token.customerAccessToBookingPaymentRequests) {
    next()
  } else {
    next({ name: 'not-found' })
  }
}

const checkCustomerAccessToBookingOrPaymentRequests = (next) => {
  const token = getToken('CustomerToken')
  if (token.customerAccessToBooking || token.customerAccessToBookingPaymentRequests) {
    next()
  } else {
    next({ name: 'not-found' })
  }
}

createMiddleware('require-auth', (args, to, from, next) => {
  const token = getToken('CustomerToken')
  const urlAfterLogout = (token && token.marketId) ? `${webConfig.baseURL}/${token.marketId}` :
    webConfig.baseURL
  if (!store.state.user.isAuthenticated) {
    store.dispatch(actionTypes.INIT_USER, 'CustomerToken')
      .then(() => {
        if (!store.state.user.isAuthenticated) {
          store.dispatch(actionTypes.LOGOUT_CUSTOMER, urlAfterLogout)
        } else {
          if (!token || !token.accessToken) {
            store.dispatch(actionTypes.LOGOUT_CUSTOMER, urlAfterLogout)
            return
          }
          checkMarketId(token)
          next()
        }
      })
      .catch((error) => {
        if ((error.response && error.response.status === 401) ||
                    !store.state.user.isAuthenticated) {
          if (/extend_booking_request|booking_payment_request/.test(to.fullPath)) {
            window.location.replace(to.fullPath)
          } else {
            store.dispatch(actionTypes.LOGOUT_CUSTOMER, urlAfterLogout)
          }
        } else {
          next()
        }
      })
  } else {
    next()
  }
})

createMiddleware('check-media-id', (args, to, from, next) => {
  store.dispatch(actionTypes.GET_MARKET_INSTAGRAM_MEDIA, to.params.mediaId)
    .then(() => {
      if (store.state.marketInstagramMedia) {
        if (!store.state.marketInstagramMedia.mediaId)
          next({ name: 'not-found' })
        else
          next()
      }
      else
        next({ name: 'not-found' })
    })
    .catch(() => {
      next({ name: 'not-found' })
    })
})

createMiddleware('check-extension-booking-request-id', (args, to, from, next) => {
  store.dispatch(actionTypes.GET_BOOKING_REQUEST, to.params.bookingRequestId)
    .then(() => {
      if (store.state.selectedBookingRequest) {
        if (!store.state.selectedBookingRequest.id) {
          Vue.notify({
            group: 'app',
            type : 'error',
            title: i18n.t('notifications.title.error'),
            text: i18n.t('notifications.text.booking_requests.error_extend_booking_request_invalid_link')
          })
          next({name: 'not-found'})
        } else if (store.state.selectedBookingRequest.status === 'expired'
          || (!store.state.selectedBookingRequest.isPaymentRequest
          && !canExtendBookingRequest(store.state.selectedBookingRequest)
          && !canContinueBookingRequestExtension(store.state.selectedBookingRequest))) {
          if (store.state.selectedBookingRequest.lastActiveChildId) {
            next({name: 'extend-booking-request', params: { bookingRequestId: store.state.selectedBookingRequest.lastActiveChildId }, query: to.query})
          } else {
            Vue.notify({
              group: 'app',
              type: 'error',
              title: i18n.t('notifications.title.error'),
              text: i18n.t('notifications.text.booking_requests.error_booking_request_already_extended')
            })
            next({name: 'bookings'})
          }
        } else {
          next()
        }
      }
      else {
        Vue.notify({
          group: 'app',
          type : 'error',
          title: i18n.t('notifications.title.error'),
          text: i18n.t('notifications.text.booking_requests.error_extend_booking_request_invalid_link')
        })
        next({name: 'not-found'})
      }
    })
    .catch(() => {
      Vue.notify({
        group: 'app',
        type : 'error',
        title: i18n.t('notifications.title.error'),
        text: i18n.t('notifications.text.booking_requests.error_extend_booking_request_invalid_link')
      })
      next({ name: 'not-found' })
    })
})

createMiddleware('check-booking-payment-request-id', (args, to, from, next) => {
  store.dispatch(actionTypes.GET_BOOKING_REQUEST, to.params.bookingRequestId)
    .then(() => {
      if (store.state.selectedBookingRequest) {
        if (!store.state.selectedBookingRequest.id) {
          Vue.notify({
            group: 'app',
            type : 'error',
            title: i18n.t('notifications.title.error'),
            text: i18n.t('notifications.text.booking_requests.error_payment_request_invalid_link')
          })
          next({name: 'not-found'})
        } else if (!store.state.selectedBookingRequest.isPaymentRequest
            && !canFinalizeBookingPaymentRequest(store.state.selectedBookingRequest)
                && !canContinueFinalizeBookingPaymentRequest(store.state.selectedBookingRequest)) {
          if (store.state.selectedBookingRequest.lastActiveChildId) {
            next({name: 'booking-payment-request', params: { bookingRequestId: store.state.selectedBookingRequest.lastActiveChildId }, query: to.query})
          } else {
            Vue.notify({
              group: 'app',
              type: 'error',
              title: i18n.t('notifications.title.error'),
              text: i18n.t('notifications.text.booking_requests.error_payment_request_already_paid')
            })
            next({name: 'bookings'})
          }
        } else {
          next()
        }
      }
      else {
        Vue.notify({
          group: 'app',
          type : 'error',
          title: i18n.t('notifications.title.error'),
          text: i18n.t('notifications.text.booking_requests.error_payment_request_invalid_link')
        })
        next({name: 'not-found'})
      }
    })
    .catch(() => {
      Vue.notify({
        group: 'app',
        type : 'error',
        title: i18n.t('notifications.title.error'),
        text: i18n.t('notifications.text.booking_requests.error_payment_request_invalid_link')
      })
      next({ name: 'not-found' })
    })
})

const routes = [
  ...middleware('require-auth', [{
    path: '/',
    name: 'base',
    component: BasePage,
    redirect: 'sales',
    children: [
      {
        path: 'sales',
        name: 'sales',
        beforeEnter: (to, from, next) => {
          checkMarketAccess(next)
        },
        component: SalesView,
      },
      {
        path: 'product-sheets',
        component: ProductSheetBaseView,
        children: [
          {
            path: '',
            name: 'product-sheets',
            beforeEnter: (to, from, next) => {
              checkMarketAccess(next)
              checkIndividualizedItemsAccess(next)
              checkCustomerAccessToProductCreation(next)
            },
            component: ProductSheetsView,
          },
          {
            path: ':productSheetId/detail',
            name: 'product-sheet-details',
            beforeEnter: (to, from, next) => {
              checkMarketAccess(next)
              checkIndividualizedItemsAccess(next)
              checkCustomerAccessToProductCreation(next)
            },
            component: ProductSheetDetailView,
          },
          // Route for Product Sheet detail page
          {
            path: ':productSheetId/detail/:productId/sales',
            name: 'product-sheet-product-sale-lines',
            beforeEnter: (to, from, next) => {
              checkMarketAccess(next)
              checkIndividualizedItemsAccess(next)
              checkCustomerAccessToProductCreation(next)
            },
            component: ProductSalesView,
          },
          // Route for Products page
          {
            path: ':productSheetId/products/:productId/sales',
            name: 'product-sale-lines',
            beforeEnter: (to, from, next) => {
              checkMarketAccess(next)
              checkIndividualizedItemsAccess(next)
            },
            component: ProductSalesView,
          },
        ]
      },
      {
        path: 'create_booking',
        name: 'create-booking',
        beforeEnter: (to, from, next) => {
          checkMarketAccess(next)
          checkOnlineBookingFeature(next)
          checkGatewayValidOrOfflinePayments(next)
          checkShowPaymentTerms(next)
          checkCustomerAccessToBooking(next)
        },
        component: CreateBookingView,
        props: { isCustomerBooking: true },
      },
      {
        path: 'market_terms',
        name: 'market-terms',
        beforeEnter: (to, from, next) => {
          checkMarketAccess(next)
          checkOnlineBookingFeature(next)
          checkShowPaymentTerms(next)
        },
        component: MarketTermsView,
        props: { isCustomerView: true },
      },
      {
        path: 'products',
        name: 'products',
        beforeEnter: (to, from, next) => {
          checkMarketAccess(next)
          checkIndividualizedItemsAccess(next)
        },
        component: ProductsView,
      },
      {
        path: 'payments',
        name: 'payments',
        beforeEnter: (to, from, next) => {
          checkMarketAccess(next)
          checkPaymentsFeature(next)
        },
        component: PaymentReportView,
      },
      {
        path: 'bookings',
        name: 'bookings',
        beforeEnter: (to, from, next) => {
          checkMarketAccess(next)
          checkOnlineBookingFeature(next)
          checkCustomerAccessToBookingOrPaymentRequests(next)
        },
        component: BookingRequestView,
      },
      ...middleware('check-extension-booking-request-id', [
        {
          path: 'extend_booking_request/:bookingRequestId',
          name: 'extend-booking-request',
          beforeEnter: (to, from, next) => {
            checkMarketAccess(next)
            checkOnlineBookingFeature(next)
            checkGatewayValidOrOfflinePayments(next)
            checkCustomerAccessToBooking(next)
          },
          component: ExtendBookingView,
          props: { isCustomerView: true },
        },
      ]),
      ...middleware('check-booking-payment-request-id', [
        {
          path: 'booking_payment_request/:bookingRequestId',
          name: 'booking-payment-request',
          beforeEnter: (to, from, next) => {
            checkMarketAccess(next)
            checkOnlineBookingFeature(next)
            checkGatewayApiValid(next)
            checkCustomerAccessToBookingPaymentRequests(next)
          },
          component: BookingPaymentRequestView,
          props: { isCustomerView: true },
        },
      ]),
      {
        path: 'settings',
        name: 'settings',
        beforeEnter: (to, from, next) => {
          checkMarketAccess(next)
        },
        component: SettingsView,
      },
      ...middleware('check-media-id', [
        {
          path: 'post/loppeonline/:mediaId',
          name: 'media-page',
          component: InstagramMediaView,
        },
      ]),
    ]
  },
  ]),
  {
    path: '/404',
    name: 'not-found',
    component: NotFoundPage,
  },
  {
    path: '*',
    redirect: '/404'
  }
]

const router = new VueRouter({ routes })


Vue.use(VueRouter)
Vue.use(VueRouterMiddleware, { router })

export default router
